/**
 * What kind of verification a record is.
 *
 * Nouns, deliberately. These used to be past participles, which put
 * `verificationType: 'attested'` beside `status: 'approved'` and left a reader asking
 * which of the two was the state. A type is what the thing is; the status is where it
 * has got to.
 *
 * Kept as a separate copy of the vocabulary in the `verificationRecord` layer module
 * for the same reason `documentIntent.ts` is: the two live either side of a
 * TypeScript/CommonJS boundary that nothing in this repo bridges. The strings are the
 * contract between them.
 */
export const VERIFICATION_TYPES = ['request', 'attestation', 'authentication', 'issuance'] as const;

export type VerificationType = (typeof VERIFICATION_TYPES)[number];

/**
 * Values written before the vocabulary changed. Records still carry them and nothing
 * backfills: production held 16 verification records and 13 document copies at the
 * time, so a permanent map is cheaper and safer than a migration, and it keeps working
 * for any record however old.
 */
const LEGACY_VERIFICATION_TYPES: Record<string, VerificationType> = {
	requested: 'request',
	attested: 'attestation',
	authenticated: 'authentication',
	issued: 'issuance'
};

/**
 * The stored value as today's vocabulary, whether it was written before or after.
 *
 * Every read goes through this. Comparing a raw stored value against a noun silently
 * fails for older records, and the two places that compare are gates on partner
 * delivery, where a silent failure means a document that should be shareable is not.
 */
export const normaliseVerificationType = (value?: string | null): VerificationType | undefined => {
	if (typeof value !== 'string') return undefined;
	const legacy = LEGACY_VERIFICATION_TYPES[value];
	if (legacy) return legacy;
	return (VERIFICATION_TYPES as readonly string[]).includes(value) ? (value as VerificationType) : undefined;
};
